import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, finalize, map, Observable, switchMap, tap } from 'rxjs';

import { environment } from 'src/environments/environment';
import { IImpersonatedAuthenticationResponseModelData, IImpersonationResponseModelData, ImpersonatedAuthenticationResponseModel, ImpersonationResponseModel } from '../models/impersonation.model';
import { UserModel } from '../models/user.model';

import { AuthHttpService } from './auth-http.service';
import { AuthService } from './auth.service';
import { AppLoggingService } from 'src/app/_shared/core/services/app-logging.service';
import { LoggerCommonService } from 'src/app/_shared/core/services/logger-common.service';
import { OPERATIONS, PAGE } from 'src/app/_shared/core/constants/app-insight-constants';

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {

  private isLoadingSubject: BehaviorSubject<boolean>;

  constructor(
    private authHttpService: AuthHttpService,
    private authService: AuthService,
    private cookieService: CookieService,
    private readonly apploggingService: AppLoggingService,
    private readonly logger: LoggerCommonService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
  }

  get isLoading$() {
    return this.isLoadingSubject.asObservable();
  }

  impersonatedLogin(token: string): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.impersonatedLogin(token).pipe(
      map((response: IImpersonatedAuthenticationResponseModelData) => {
        if (!response?.result) {
          throw Error("Failed to impersonate");
        }

        this.setAuthInLocalStorage(response.result);
        const expiry = response.result.expireInSeconds / 86400;
        return { expiry, tokenResponse: response.result };
      }),
      switchMap(({ expiry }) => 
        this.authService.getUser().pipe(
            map(user => ({ user, expiry }))
        )
    ),
    tap(({ user, expiry }) => {
        if (user?.id) {
            this.cookieService.set(environment.userIdKey, user.id.toString(), expiry, '/');
        }

        // Additional logging after getting the user
        this.apploggingService.setUserId(user?.id.toString(), user?.tenant?.id.toString(), user);
        this.logger.trace(PAGE.LogIn, OPERATIONS.LoggedInWithImpersonation, user.name, 'User logged in successfully');
    }),
    map(({ user }) => user),
    finalize(() => this.isLoadingSubject.next(false))
  );
 }

  unimpersonate(): Observable<ImpersonationResponseModel> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.unimpersonate().pipe(
      map((response: IImpersonationResponseModelData) => {
        if (!response?.result) {
          throw Error("Failed to unimpersonate");
        }

        this.logger.trace(PAGE.LogOut, OPERATIONS.LoggedOutWithImpersonation, this.authService.currentUserValue.name, 'User logged out successfully');
        this.apploggingService.clearUserId();
        this.authService.logout();

        return response.result;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  private setAuthInLocalStorage(response: ImpersonatedAuthenticationResponseModel): void {
    const expiry = response.expireInSeconds / 86400;
    this.cookieService.set(environment.tokenKey, response.accessToken, expiry,'/');
  }
}
